import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { CreateComponent } from '../create/create.component';
import { DetailsComponent } from '../details/details.component';

import { ChangeNotificationEmailPage } from '../../../modals/manage-team/change-notification-email/change-notification-email.page';
import { AlertController, ModalController, ToastController } from '@ionic/angular'
import { ActivityLogPage } from '../../../modals/manage-team/activity-log/activity-log.page';
import { AddLogoPage } from '../../../modals/manage-team/add-logo/add-logo.page';
import { ChangeUserPermissionsPage } from '../../../modals/manage-team/change-user-permissions/change-user-permissions.page';
import { EditUserPage } from '../../../modals/manage-team/edit-user/edit-user.page';
import { GlobalService } from '../../../api/global.service';
import { ApiService } from '../../../api/api.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-team-members-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit {
    loading = false;
    @Input() team_member_roles = [];
    @Input() show_company_role = false;
    @Input() team_members = [];
    @Input() allow_add_team_members = false;
    @Input() allow_delete_team_members = false;
    show_deleted = false;

    /**
     * Array of column keys to hide. Example: ['email','phone'] to hide email and phone columns
     */
    @Input() hideColumns: string[] = [];
    matchModeOptions = [];
    skills = [];
    finished_loading = false;
    current_user: any = null;

    // Licenses owned/used - only fetched for an admin who can actually add team members (see
    // Refresh()), used both for the summary sentence at the top of the list and to head off
    // opening the New Team Member modal at all when there's no license left for it.
    firm_user_licenses: number = null;
    firm_used_licenses: number = null;

    get outOfLicenses(): boolean {
        return this.firm_user_licenses != null && this.firm_used_licenses >= this.firm_user_licenses;
    }

    @ViewChild('dt') teamMembersTable: Table;
    activeTeamMemberFilterLabels: string[] = [];
    private readonly teamMemberFilterLabelMap: { [field: string]: string } = {
      display_name: 'Name',
      email: 'Email',
      username: 'Username',
      company_role: 'Role',
      active: 'Active',
      is_admin: 'Admin',
      outlook_connected: 'Outlook Linked',
    };

    onTeamMemberTableFilter(event: any) {
      this.activeTeamMemberFilterLabels = this.computeActiveFilterLabels(event.filters, this.teamMemberFilterLabelMap);
    }

    clearTeamMemberFilters() {
      if (this.teamMembersTable) this.clearTableFilters(this.teamMembersTable);
      this.activeTeamMemberFilterLabels = [];
    }

    private clearTableFilters(t: Table): void {
      if (!t) return;
      (t as any).tableService.onResetChange();
      if ((t as any).isStateful()) (t as any).clearState();
      (t as any)._filter();
    }

    private computeActiveFilterLabels(filters: any, labelMap: { [field: string]: string }): string[] {
      if (!filters) return [];
      const active: string[] = [];
      for (const field of Object.keys(labelMap)) {
        const raw = filters[field];
        if (!raw) continue;
        const constraints = Array.isArray(raw) ? raw : [raw];
        const hasValue = constraints.some((c: any) => {
          if (c.value === null || c.value === undefined) return false;
          if (Array.isArray(c.value)) return c.value.length > 0;
          if (typeof c.value === 'string') return c.value.trim().length > 0;
          return true;
        });
        if (hasValue) active.push(labelMap[field]);
      }
      return active;
    }

    constructor(private modalController: ModalController, public globalService: GlobalService, private apiService: ApiService, private alertController: AlertController, private changeDetectorRef: ChangeDetectorRef, private router: Router) { }

    ngOnInit() {
        this.Refresh();
    }

    Refresh() {
        this.current_user = this.globalService.GetCurrentUser();
        if (!this.team_members || this.team_members.length == 0) this.GetTeamMembers();
        else this.GetTeamMembersDetails();
        // License counts come from firms/admin/details, which is firm admin only (the API enforces the license limit either way)
        if (this.allow_add_team_members && this.current_user?.is_admin) this.GetLicenseInfo();
    }

    async GetLicenseInfo() {
        const data: any = await this.apiService.get('firms/admin/details');
        if (data.success && data.data?.[0]) {
            this.firm_user_licenses = Number(data.data[0].user_licenses);
            this.firm_used_licenses = Number(data.data[0].used_licenses);
        }
    }

    async GetTeamMembersDetails() {
        for (let i = 0; i < this.team_members.length; i++) {
            let team_member = this.team_members[i];
            let data: any = await this.apiService.get(`team_members/${team_member.guid}`);
            console.log(data.data[0]);
            if (data.success) {
                // Merge existing properties with incoming data, preferring new data
                this.team_members[i] = { ...team_member, ...data.data[0] };
            }
        }
        this.finished_loading = true;
    }

    GetRoleName(role_guid){
        if(!this.team_member_roles) return "";
        let role = this.team_member_roles[this.globalService.GetArrayIndex(this.team_member_roles,role_guid)];
        console.log("Role",role);
        return role.title;

    }

    async GetTeamMembers() {
        let data: any = await this.apiService.get('team_members');
        if (data.success) {
            this.team_members = data.data;
            console.log(this.team_members);
        }
        this.finished_loading = true;
    }

    async presentNewTeamUserModal() {
        // Re-check right before opening rather than trusting whatever GetLicenseInfo() last
        // loaded - avoids the create form popping up (and, per the report this replaced, hanging)
        // only to fail once submitted because a license was used up elsewhere in the meantime.
        await this.GetLicenseInfo();
        if (this.outOfLicenses) {
            const alert = await this.alertController.create({
                header: 'Out of Licenses',
                message: `Your firm is using all ${this.firm_user_licenses} of its team member license(s). Add more licenses from Manage Firm &gt; Billing before adding another team member.`,
                buttons: [
                    { text: 'Close', role: 'cancel' },
                    {
                        text: 'Go to Billing',
                        handler: () => {
                            this.router.navigate(['/manage-firm/billing']);
                        }
                    }
                ]
            });
            await alert.present();
            return;
        }

        const modal = await this.modalController.create({
            component: CreateComponent,
            cssClass: '',
            componentProps: {
            },
        })
        modal.onDidDismiss().then((data: any) => {
            if (data && data.data) {
                this.GetTeamMembers();
                this.GetLicenseInfo();
                console.log(data.data.data[0]);
                this.presentTeamMembersDetailsModal(data.data.data[0]);
            }

        });
        return await modal.present()
    }


    async presentActivityLogModal(team_member) {
        const modal = await this.modalController.create({
            component: ActivityLogPage,
            componentProps: {
                guid: team_member.guid,
            },
        })
        return await modal.present()
    }



    async presentTeamMembersDetailsModal(team_member) {
        const modal = await this.modalController.create({
            component: DetailsComponent,
            cssClass: 'large-modal',
            componentProps: {
                team_member: team_member,
            },
        })
        modal.onDidDismiss().then((data: any) => {
            if (data && data.data) {
                team_member.display_name = data.data.display_name;
                team_member.skills = JSON.parse(JSON.stringify(data.data.skills));
            }

        });

        return await modal.present()
    }

    async presentCloudAccessMessage(team_member) {

        // Admins, or team_members_update - but a non-admin can never change a firm admin (the API enforces the same)
        if (!this.current_user.is_admin && (!this.globalService.Can('team_members_update') || team_member.is_admin == 1)) {
            this.globalService.presentToastMessage("You don't have permission to change this team member's active status", false);
            return;
        }
        
        if(team_member.guid == this.current_user.guid) {
            this.globalService.presentToastMessage("You cannot change your own active status", false);
            return;
        }

        // Only activating (not deactivating) consumes a license - re-check fresh rather than
        // trusting whatever GetLicenseInfo() last loaded, same reasoning as presentNewTeamUserModal().
        if (team_member.active == 0) {
            await this.GetLicenseInfo();
            if (this.outOfLicenses) {
                this.globalService.presentToastMessage(`Your firm is using all ${this.firm_user_licenses} of its team member license(s). Add more from Manage Firm > Billing before activating another team member.`, false);
                return;
            }
        }

        const alert = await this.alertController.create({
            backdropDismiss: false,
            header: 'Are you sure you would like to change the active status?',
            message: `This user is currently active`,
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                    handler: () => {
                        console.log("Cancel: " + team_member.active);
                    },
                },
                {
                    text: 'OK',
                    role: 'confirm',
                    handler: async () => {
                        if(team_member.active == 1) team_member.active = 0;
                        else if (team_member.active == 0) team_member.active = 1;
                        let data: any = await this.apiService.put(`team_member/${team_member.guid}/activate`, team_member);
                        this.globalService.presentToastMessage(data.message, data.success);
                        if (data.success) {
                            this.GetLicenseInfo();
                        }
                        else {
                            // Failed (e.g. out of licenses) - revert the toggle back to its prior state
                            if (team_member.active == 1) team_member.active = 0;
                            else if (team_member.active == 0) team_member.active = 1;
                            this.RefreshUI();
                        }

                    },
                },
            ],
        });

        await alert.present();
    }

    RefreshUI() {
        this.changeDetectorRef.detectChanges();
        // Forces Angular to update the view
    }

    async DeleteTeamMember(team_member) {
        const alert = await this.alertController.create({
      header: 'Delete Team Member',
      message: `Are you sure you want to delete <strong>${team_member.display_name}</strong>? This will deactivate them from the system and they will no longer be able to log in. <b>If you will give them access again in the future its recommended to just make them inactive instead of deleting them.</b>`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Delete',
          cssClass: 'alert-button-danger',
          handler: async () => {
            await this.globalService.presentLoading('Deleting contact...', 9000000);
            const data: any = await this.apiService.delete(`team_members/${team_member.guid}`, {});
            this.globalService.dismissLoading();
            if (data.success) {
              team_member.active = 0;
              team_member.deleted = 1;
              if (!this.show_deleted) this.team_members = this.team_members.filter(tm => tm.guid !== team_member.guid);
              this.RefreshUI();
            } else {
              this.globalService.presentToastMessage(data.message, false);
            }

          }
        }
      ]
    });
    await alert.present();
    }

    async RestoreTeamMember(team_member) {
        const alert = await this.alertController.create({
      header: 'Restore Team Member',
      message: `Are you sure you want to restore <strong>${team_member.display_name}</strong>? They will remain inactive until you turn their access back on.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Restore',
          handler: async () => {
            await this.globalService.presentLoading('Restoring team member...', 9000000);
            const data: any = await this.apiService.put(`team_members/${team_member.guid}/restore`, {});
            this.globalService.dismissLoading();
            if (data.success) {
              team_member.deleted = 0;
              this.RefreshUI();
            } else {
              this.globalService.presentToastMessage(data.message, false);
            }

          }
        }
      ]
    });
    await alert.present();
    }

    async ToggleShowDeleted() {
        if (this.show_deleted) await this.GetDeletedTeamMembers();
        else this.team_members = this.team_members.filter(tm => !tm.deleted);
    }

    async GetDeletedTeamMembers() {
        const data: any = await this.apiService.get('team_members?show_deleted=1');
        if (data.success) {
            const existing_guids = new Set(this.team_members.map(tm => tm.guid));
            const deleted_members = (data.data || []).filter(tm => !existing_guids.has(tm.guid));
            this.team_members = [...this.team_members, ...deleted_members];
        }
    }

}
